/**
 * Toast hooks and helpers.
 *
 * The legacy API in the monolith is:
 *   showToast(type, title, message, duration)
 *   toastSuccess(title, message, duration)
 *   toastError(title, message, duration)
 *   toastWarning(title, message, duration)
 *   toastInfo(title, message, duration)
 *
 * We expose `useToast()` which returns the same five helpers bound to
 * the Zustand store so components can do:
 *   const toast = useToast();
 *   toast.success('Saved', 'Group SMTP updated');
 *
 * The standalone module-level helpers (`toast`, `toastSuccess`, ...)
 * are also exported so non-React code (utility modules, event handlers
 * defined outside components) can fire toasts.
 */

import { useUiStore } from '@/state/uiStore.js';

/**
 * @typedef {import('@/state/uiStore.js').ToastType} ToastType
 */

/** Module-level entry — works outside React components. */
export function showToast(/** @type {ToastType} */ type, /** @type {string} */ title, /** @type {string} */ message, /** @type {number | { duration?: number, actionLabel?: string, onAction?: () => void }} */ duration) {
  return useUiStore.getState().pushToast(type, title, message, duration);
}

export const toastSuccess = (/** @type {string} */ title, /** @type {string} */ message, /** @type {number | { duration?: number, actionLabel?: string, onAction?: () => void }} */ duration) =>
  showToast('success', title, message, duration);
export const toastError = (/** @type {string} */ title, /** @type {string} */ message, /** @type {number | { duration?: number, actionLabel?: string, onAction?: () => void }} */ duration) =>
  showToast('error', title, message, duration);
export const toastWarning = (/** @type {string} */ title, /** @type {string} */ message, /** @type {number | { duration?: number, actionLabel?: string, onAction?: () => void }} */ duration) =>
  showToast('warning', title, message, duration);
export const toastInfo = (/** @type {string} */ title, /** @type {string} */ message, /** @type {number | { duration?: number, actionLabel?: string, onAction?: () => void }} */ duration) =>
  showToast('info', title, message, duration);

/**
 * Hook that returns a stable object of toast helpers. Re-renders only if
 * the dismiss function reference changes (which it won't — Zustand
 * actions are stable).
 */
export function useToast() {
  const pushToast = useUiStore((s) => s.pushToast);
  const dismissToast = useUiStore((s) => s.dismissToast);
  return {
    /** @param {ToastType} type @param {string} title @param {string} [message] @param {number | { duration?: number, actionLabel?: string, onAction?: () => void }} [duration] */
    show: (type, title, message, duration) => pushToast(type, title, message, duration),
    success: (/** @type {string} */ title, /** @type {string} */ message, /** @type {number | { duration?: number, actionLabel?: string, onAction?: () => void }} */ duration) =>
      pushToast('success', title, message, duration),
    error: (/** @type {string} */ title, /** @type {string} */ message, /** @type {number | { duration?: number, actionLabel?: string, onAction?: () => void }} */ duration) =>
      pushToast('error', title, message, duration),
    warning: (/** @type {string} */ title, /** @type {string} */ message, /** @type {number | { duration?: number, actionLabel?: string, onAction?: () => void }} */ duration) =>
      pushToast('warning', title, message, duration),
    info: (/** @type {string} */ title, /** @type {string} */ message, /** @type {number | { duration?: number, actionLabel?: string, onAction?: () => void }} */ duration) =>
      pushToast('info', title, message, duration),
    dismiss: dismissToast
  };
}
